import { Link } from '@tanstack/react-router';
import { Check, ImageIcon, Inbox, PenLine, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { allowedActions } from '@marketingbee/domain/marketing';
import { Alert, Button, Card, CardContent, Dialog, DialogContent, EmptyState, Field, Input, PageHeader, Textarea, useToast } from '@marketingbee/ui';
import { QueryView, Section } from '../components/common';
import { audienceName, ChannelBadge, formatInZone, IssueList, pillarName, StatusPill, useActiveBrand, useCanManageContent, type BrandRow, type ContentRow } from '../components/marketing';
import { errorMessage, trpc } from '../lib/trpc';

/** Approval queue: everything waiting for a person, plus human-only slots still to be written. */
export function ReviewPage() {
  const { brands } = useActiveBrand();
  const queue = trpc.content.list.useQuery({ statuses: ['in_review', 'changes_requested'], limit: 200 });
  const toWrite = trpc.content.list.useQuery({ statuses: ['draft'], limit: 200 });
  const brandOf = (id: string) => brands.find((b) => b.id === id);
  const sorted = (rows: ContentRow[]) => [...rows].sort((a, b) => (b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0) - (a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0));
  const humanDrafts = (toWrite.data ?? []).filter((r) => r.humanOnly && !r.body.trim());
  return (
    <div className="space-y-8">
      <PageHeader title="Review" description="Nothing is published until someone approves it. Approving content with a time schedules it." />
      <QueryView query={queue} label="Loading the review queue">
        {(rows) =>
          rows.length === 0 ? (
            <Card>
              <EmptyState
                icon={<Inbox />}
                title="Nothing waiting"
                description="Nothing waiting. Plan a week from the Calendar."
                action={
                  <Button asChild variant="outline">
                    <Link to="/calendar">Open the Calendar</Link>
                  </Button>
                }
              />
            </Card>
          ) : (
            <div className="space-y-4">
              {sorted(rows).map((row) => (
                <ReviewCard key={row.id} row={row} brand={brandOf(row.brandId)} />
              ))}
            </div>
          )
        }
      </QueryView>
      {humanDrafts.length ? (
        <Section title="Write these yourself" description="These pillars are written by a person, so MarketingBee never drafts them. Write the post, then send it for review.">
          <div className="space-y-4">
            {sorted(humanDrafts).map((row) => (
              <ReviewCard key={row.id} row={row} brand={brandOf(row.brandId)} />
            ))}
          </div>
        </Section>
      ) : null}
    </div>
  );
}

type CommentKind = 'request_changes' | 'reject' | 'redraft';

export function ReviewCard({ row, brand }: { row: ContentRow; brand: BrandRow | undefined }) {
  const tz = brand?.profile.timezone ?? 'America/Toronto';
  const canManage = useCanManageContent();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const action = trpc.content.action.useMutation();
  const redraft = trpc.content.redraft.useMutation();
  const update = trpc.content.update.useMutation();
  const [dialog, setDialog] = useState<CommentKind | null>(null);
  const [comment, setComment] = useState('');
  const [text, setText] = useState(row.body);
  const actions = allowedActions(row.status);
  const pillar = pillarName(brand?.profile, row.pillarId);
  const audience = audienceName(brand?.profile, row.audienceId);
  const issues = row.issues ?? [];
  const blocked = issues.some((i) => i.severity === 'block');

  const refresh = async () => {
    await Promise.all([utils.content.list.invalidate(), utils.content.get.invalidate({ id: row.id }), utils.dashboard.summary.invalidate()]);
  };
  const run = (a: 'approve' | 'request_changes' | 'reject' | 'submit', c?: string) =>
    action.mutate(
      { contentId: row.id, action: a, comment: c || undefined },
      {
        onSuccess: async (r) => {
          success(a === 'approve' ? (r.status === 'scheduled' ? 'Approved and scheduled' : 'Approved') : a === 'submit' ? 'Sent for review' : a === 'reject' ? 'Rejected' : 'Changes requested');
          setDialog(null);
          setComment('');
          await refresh();
        },
        onError: (e) => error('Could not do that', errorMessage(e)),
      },
    );
  const saveHumanText = async () => {
    try {
      await update.mutateAsync({ contentId: row.id, body: text });
      await action.mutateAsync({ contentId: row.id, action: 'submit' });
      success('Saved and sent for review');
      await refresh();
    } catch (e) {
      error('Could not save', errorMessage(e));
    }
  };

  return (
    <Card>
      <CardContent className="space-y-4 py-5">
        <div className="flex flex-wrap items-center gap-2">
          <ChannelBadge channel={row.channel} />
          <StatusPill status={row.status} />
          <span className="text-[13px] text-muted-foreground">{formatInZone(row.scheduledAt, tz)}</span>
          {pillar ? <span className="text-[13px] text-muted-foreground">· {pillar}</span> : null}
          {audience ? <span className="text-[13px] text-muted-foreground">· for {audience}</span> : null}
          <span className="ml-auto">
            <Button asChild size="sm" variant="ghost">
              <Link to="/content/$id" params={{ id: row.id }}>
                <PenLine /> Edit
              </Link>
            </Button>
          </span>
        </div>

        {row.reviewComment && row.status === 'changes_requested' ? <Alert variant="warning" title="Changes requested">{row.reviewComment}</Alert> : null}

        {row.humanOnly ? (
          <div className="space-y-2">
            <p className="text-[14px] font-medium">Write this one yourself</p>
            <p className="text-[13px] text-muted-foreground">{pillar ? `"${pillar}" ` : 'This pillar '}is written by a person, in your own words.</p>
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} aria-label="Your post" />
            <Button size="sm" disabled={!text.trim() || text === row.body} loading={update.isPending || (action.isPending && action.variables?.action === 'submit')} onClick={() => void saveHumanText()}>
              Save and send for review
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {row.title ? <h3 className="text-[16px] font-semibold">{row.title}</h3> : null}
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed">{row.body || <span className="text-muted-foreground">Waiting for the writer.</span>}</p>
            {row.hashtags.length ? <p className="text-[13px] text-info">{row.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}</p> : null}
          </div>
        )}

        {row.imageIdea ? (
          <p className="flex gap-2 text-[13px] text-muted-foreground">
            <ImageIcon className="mt-0.5 size-4 shrink-0" aria-hidden /> <span><span className="font-medium text-foreground">Image idea:</span> {row.imageIdea}</span>
          </p>
        ) : null}

        <IssueList issues={issues} />

        <div className="flex flex-wrap gap-2">
          {canManage && actions.includes('approve') ? (
            <Button size="sm" disabled={blocked} title={blocked ? 'Fix the issues marked "Must fix" first' : undefined} loading={action.isPending && action.variables?.action === 'approve'} onClick={() => run('approve')}>
              <Check /> Approve
            </Button>
          ) : null}
          {actions.includes('submit') && row.body.trim() && !row.humanOnly ? (
            <Button size="sm" variant="outline" loading={action.isPending && action.variables?.action === 'submit'} onClick={() => run('submit')}>
              Send for review
            </Button>
          ) : null}
          {canManage && actions.includes('request_changes') ? (
            <Button size="sm" variant="outline" onClick={() => setDialog('request_changes')}>
              Request changes
            </Button>
          ) : null}
          {!row.humanOnly && actions.includes('edit') ? (
            <Button size="sm" variant="outline" onClick={() => setDialog('redraft')}>
              <RefreshCw /> Redraft
            </Button>
          ) : null}
          {canManage && actions.includes('reject') ? (
            <Button size="sm" variant="ghost" onClick={() => setDialog('reject')}>
              <X /> Reject
            </Button>
          ) : null}
        </div>
      </CardContent>

      <Dialog open={dialog !== null} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent
          title={dialog === 'reject' ? 'Reject this post' : dialog === 'redraft' ? 'Redraft with direction' : 'Request changes'}
          description={dialog === 'redraft' ? 'Tell the writer what to change, for example "shorter" or "lead with late payments".' : dialog === 'reject' ? 'Rejected posts are never published. Say why, so the next draft is better.' : 'The writer sees your comment on the post.'}
          footer={
            <>
              <Button variant="outline" onClick={() => setDialog(null)}>
                Cancel
              </Button>
              {dialog === 'redraft' ? (
                <Button
                  loading={redraft.isPending}
                  onClick={() =>
                    redraft.mutate(
                      { contentId: row.id, direction: comment || undefined },
                      {
                        onSuccess: async () => {
                          success('New draft ready');
                          setDialog(null);
                          setComment('');
                          await refresh();
                        },
                        onError: (e) => error('Could not redraft', errorMessage(e)),
                      },
                    )
                  }
                >
                  Redraft
                </Button>
              ) : (
                <Button variant={dialog === 'reject' ? 'destructive' : 'primary'} disabled={!comment.trim()} loading={action.isPending} onClick={() => (dialog === 'reject' || dialog === 'request_changes') && run(dialog, comment)}>
                  {dialog === 'reject' ? 'Reject' : 'Request changes'}
                </Button>
              )}
            </>
          }
        >
          {dialog === 'redraft' ? (
            <Field label="Direction (optional)">
              <Input value={comment} maxLength={500} onChange={(e) => setComment(e.target.value)} placeholder="Shorter, and lead with the payment problem" />
            </Field>
          ) : (
            <Field label="Comment">
              <Textarea rows={3} value={comment} maxLength={2000} onChange={(e) => setComment(e.target.value)} />
            </Field>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
